import { commissionAmountCents } from './commission';
import { METRIC_BY_ID } from './metrics';
import {
  isPunchCardSale,
  isTreatmentFact,
  prepaidSaleAmountCents,
  recognizedTreatmentRevenueCents,
} from './revenueEvents';
import { RETENTION_FOLLOW_UP_DAYS } from './types';
import type {
  DateRange,
  PractitionerWorkingHoursContext,
  ReportColumn,
  ReportFact,
  ReportFilterState,
  ReportGrouping,
  ReportResult,
  ReportRow,
  ReportSummaryCard,
  ReportValue,
  RevenueBasis,
} from './types';

export const UNASSIGNED_PRACTITIONER_ID = '__unassigned__';
export const UNASSIGNED_SERVICE_ID = '__unassigned_service__';

export interface GenerateReportInput {
  facts: ReportFact[];
  metricIds: string[];
  filters: ReportFilterState;
  period: DateRange;
  workingHours?: PractitionerWorkingHoursContext[];
  unassignedPractitionerLabel?: string;
  unassignedServiceLabel?: string;
  totalLabel?: string;
  subtotalPrefix?: string;
}

interface AggregateContext {
  facts: ReportFact[];
  appointments: ReportFact[];
  allFacts: ReportFact[];
  basis: RevenueBasis;
  working?: PractitionerWorkingHoursContext;
  clinicWorking?: { workingMinutes: number; workingDays: number };
  retentionMode: 'practitioner' | 'clinic';
}

function isAppointment(fact: ReportFact): boolean {
  return isTreatmentFact(fact);
}

function inRange(iso: string | null, range: DateRange): boolean {
  if (!iso) return false;
  const time = new Date(iso).getTime();
  return time >= range.start.getTime() && time <= range.end.getTime();
}

export function revenueCents(fact: ReportFact, basis: RevenueBasis): number {
  return recognizedTreatmentRevenueCents(fact, basis);
}

export function productSalesCents(fact: ReportFact, basis: RevenueBasis, range: DateRange): number {
  if (fact.fact_kind !== 'product_sale') return 0;
  if (basis === 'paid') {
    if (!inRange(fact.paid_at, range)) return 0;
    return prepaidSaleAmountCents(fact, basis);
  }
  if (!inRange(fact.issued_at ?? fact.starts_at, range)) return 0;
  return prepaidSaleAmountCents(fact, basis);
}

export function applyClientFilters(
  facts: ReportFact[],
  filters: ReportFilterState,
  period: DateRange,
): ReportFact[] {
  return facts.filter((fact) => {
    if (filters.newVsReturning === 'new' && isAppointment(fact) && !fact.is_new_patient) {
      return false;
    }
    if (filters.newVsReturning === 'returning' && isAppointment(fact) && fact.is_new_patient) {
      return false;
    }

    if (fact.fact_kind === 'product_sale') {
      const inBasisPeriod =
        filters.revenueBasis === 'paid'
          ? inRange(fact.paid_at, period)
          : inRange(fact.issued_at ?? fact.starts_at, period);
      if (!inBasisPeriod) return false;
      const amount = productSalesCents(fact, filters.revenueBasis, period);
      if (filters.minRevenueCents != null && amount < filters.minRevenueCents) return false;
      if (filters.maxRevenueCents != null && amount > filters.maxRevenueCents) return false;
      return true;
    }

    if (fact.appointment_status !== 'completed') return false;
    const amount = revenueCents(fact, filters.revenueBasis);
    if (filters.minRevenueCents != null && amount < filters.minRevenueCents) return false;
    if (filters.maxRevenueCents != null && amount > filters.maxRevenueCents) return false;
    return true;
  });
}

function practitionerKey(fact: ReportFact): string {
  return fact.practitioner_id ?? UNASSIGNED_PRACTITIONER_ID;
}

function serviceKey(fact: ReportFact): string {
  return fact.service_id ?? UNASSIGNED_SERVICE_ID;
}

function uniquePatients(appointments: ReportFact[]): Set<string> {
  const ids = new Set<string>();
  for (const fact of appointments) {
    if (fact.patient_id) ids.add(fact.patient_id);
  }
  return ids;
}

function completedAppointments(appointments: ReportFact[]): ReportFact[] {
  return appointments.filter((fact) => fact.appointment_status === 'completed');
}

function ratio(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null;
  return numerator / denominator;
}

function weightedCommissionPercent(appointments: ReportFact[], basis: RevenueBasis): number | null {
  let weighted = 0;
  let total = 0;
  let any = false;
  for (const fact of appointments) {
    if (fact.commission_percent == null) continue;
    const amount = Math.max(revenueCents(fact, basis), 0);
    const weight = amount > 0 ? amount : 1;
    weighted += fact.commission_percent * weight;
    total += weight;
    any = true;
  }
  if (!any || total <= 0) return null;
  return weighted / total / 100;
}

function paymentMethodSplit(facts: ReportFact[], basis: RevenueBasis): string | null {
  const buckets = new Map<string, number>();
  for (const fact of facts) {
    const method = fact.payment_method || fact.billing_method || '—';
    const amount = revenueCents(fact, basis);
    if (amount === 0) continue;
    buckets.set(method, (buckets.get(method) ?? 0) + amount);
  }
  if (buckets.size === 0) return null;
  const total = [...buckets.values()].reduce((sum, value) => sum + value, 0);
  if (total <= 0) return null;
  return [...buckets.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([method, amount]) => `${method} ${Math.round((amount / total) * 100)}%`)
    .join(', ');
}

function mostCommonService(appointments: ReportFact[]): string | null {
  const counts = new Map<string, number>();
  for (const fact of appointments) {
    const name = fact.service_name?.trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

function retentionRate(appointments: ReportFact[], mode: 'practitioner' | 'clinic'): number | null {
  const completed = completedAppointments(appointments);
  const patients = uniquePatients(completed);
  if (patients.size === 0) return null;
  let returned = 0;
  for (const patientId of patients) {
    const rows = completed.filter((fact) => fact.patient_id === patientId);
    const anyReturned = rows.some((fact) =>
      mode === 'practitioner' ? fact.returned_to_practitioner : fact.returned_to_clinic,
    );
    if (anyReturned) returned += 1;
  }
  return returned / patients.size;
}

function metricValue(metricId: string, ctx: AggregateContext): ReportValue {
  const { appointments, facts, basis, working, clinicWorking, retentionMode } = ctx;
  const hours = working ?? clinicWorking;
  const revenueTotal = appointments.reduce((sum, fact) => sum + revenueCents(fact, basis), 0);
  const productTotal = facts
    .filter((fact) => fact.fact_kind === 'product_sale')
    .reduce(
      (sum, fact) => sum + (basis === 'paid' ? fact.paid_amount_cents : fact.invoiced_amount_cents),
      0,
    );
  const completed = completedAppointments(appointments);
  const cancelled = appointments.filter((fact) => fact.appointment_status === 'cancelled').length;
  const noShows = appointments.filter((fact) => fact.appointment_status === 'no_show').length;
  const patients = uniquePatients(appointments);
  const newPatients = uniquePatients(appointments.filter((fact) => fact.is_new_patient));
  const returningPatients = uniquePatients(
    appointments.filter((fact) => !fact.is_new_patient && fact.patient_id),
  );
  const billable = appointments.filter((fact) => revenueCents(fact, basis) > 0).length;
  const commissionTotal = appointments.reduce(
    (sum, fact) => sum + commissionAmountCents(revenueCents(fact, basis), fact.commission_percent),
    0,
  );
  const practitionerCount = new Set(appointments.map(practitionerKey)).size;

  switch (metricId) {
    case 'revenue':
      return revenueTotal;
    case 'commission_amount':
      return commissionTotal;
    case 'commission_percentage':
      return weightedCommissionPercent(appointments, basis);
    case 'average_revenue_per_appointment':
    case 'revenue_per_appointment':
      return ratio(revenueTotal, appointments.length);
    case 'billable_appointments':
      return billable;
    case 'discounts':
      return facts.reduce((sum, fact) => sum + (fact.discount_cents ?? 0), 0);
    case 'refunds':
      return facts.reduce((sum, fact) => sum + (fact.refund_cents ?? 0), 0);
    case 'product_sales':
      return productTotal;
    case 'payment_method_split':
      return paymentMethodSplit(facts, basis);
    case 'outstanding_amount':
      return facts.reduce((sum, fact) => sum + (fact.outstanding_amount_cents ?? 0), 0);
    case 'paid_amount':
      return facts.reduce((sum, fact) => {
        if (isPunchCardSale(fact)) return sum;
        return sum + (fact.paid_amount_cents ?? 0);
      }, 0);
    case 'appointment_count':
      return appointments.length;
    case 'completed_appointments':
      return completed.length;
    case 'cancelled_appointments':
      return cancelled;
    case 'no_shows':
      return noShows;
    case 'average_appointment_length':
      return ratio(
        appointments.reduce((sum, fact) => sum + (fact.duration_minutes ?? 0), 0),
        appointments.length,
      );
    case 'utilisation_rate': {
      const booked = appointments
        .filter(
          (fact) =>
            fact.appointment_status !== 'cancelled' && fact.appointment_status !== 'no_show',
        )
        .reduce((sum, fact) => sum + (fact.duration_minutes ?? 0), 0);
      return hours && hours.workingMinutes > 0 ? booked / hours.workingMinutes : null;
    }
    case 'average_appointments_per_working_day':
      return hours && hours.workingDays > 0 ? appointments.length / hours.workingDays : null;
    case 'unique_patients':
      return patients.size;
    case 'new_patients':
    case 'new_patient_acquisition':
      return newPatients.size;
    case 'returning_patients':
      return returningPatients.size;
    case 'retention_rate':
      return retentionRate(appointments, retentionMode);
    case 'average_visits_per_patient':
      return ratio(appointments.length, patients.size);
    case 'patients_seen_per_practitioner':
      return ratio(patients.size, Math.max(practitionerCount, 1));
    case 'new_patient_percentage':
      return ratio(newPatients.size, patients.size);
    case 'most_common_services':
      return mostCommonService(appointments);
    case 'service_mix':
      return ratio(appointments.length, ctx.allFacts.filter(isAppointment).length);
    case 'treatments_per_service':
      return appointments.length;
    case 'revenue_per_service':
      return revenueTotal;
    case 'average_revenue_per_service':
      return ratio(revenueTotal, new Set(appointments.map(serviceKey)).size);
    case 'average_appointments_per_patient_by_service':
      return ratio(appointments.length, patients.size);
    case 'revenue_per_working_day':
      return hours && hours.workingDays > 0 ? revenueTotal / hours.workingDays : null;
    case 'revenue_per_practitioner':
      return ratio(revenueTotal, Math.max(practitionerCount, 1));
    case 'appointment_completion_rate':
      return ratio(completed.length, appointments.length);
    case 'cancellation_rate':
      return ratio(cancelled, appointments.length);
    case 'no_show_rate':
      return ratio(noShows, appointments.length);
    case 'returning_patient_percentage':
      return ratio(returningPatients.size, patients.size);
    default:
      return null;
  }
}

function valuesFor(metricIds: string[], ctx: AggregateContext): Record<string, ReportValue> {
  const values: Record<string, ReportValue> = {};
  for (const id of metricIds) {
    values[id] = metricValue(id, ctx);
  }
  return values;
}

function sumWorking(
  hours: PractitionerWorkingHoursContext[] | undefined,
  practitionerIds: string[],
): PractitionerWorkingHoursContext | undefined {
  if (!hours || hours.length === 0) return undefined;
  const selected =
    practitionerIds.length === 0
      ? hours
      : hours.filter((item) => practitionerIds.includes(item.practitionerId));
  if (selected.length === 0) return undefined;
  return {
    practitionerId: practitionerIds[0] ?? 'clinic',
    workingMinutes: selected.reduce((sum, item) => sum + item.workingMinutes, 0),
    workingDays: selected.reduce((sum, item) => sum + item.workingDays, 0),
  };
}

function makeRow(
  input: Omit<ReportRow, 'values' | 'factCount'> & {
    facts: ReportFact[];
    ctxBase: Omit<AggregateContext, 'facts' | 'appointments'>;
  },
  metricIds: string[],
): ReportRow {
  const appointments = input.facts.filter(isAppointment);
  const values = valuesFor(metricIds, {
    ...input.ctxBase,
    facts: input.facts,
    appointments,
  });
  return {
    id: input.id,
    label: input.label,
    depth: input.depth,
    grouping: input.grouping,
    practitionerId: input.practitionerId,
    serviceId: input.serviceId,
    children: input.children,
    values,
    factCount: input.facts.length,
  };
}

function groupLabel(
  facts: ReportFact[],
  kind: 'practitioner' | 'service',
  fallbackPractitioner: string,
  fallbackService: string,
): string {
  if (kind === 'practitioner') {
    return (
      facts.find((fact) => fact.practitioner_name)?.practitioner_name?.trim() ||
      fallbackPractitioner
    );
  }
  return facts.find((fact) => fact.service_name)?.service_name?.trim() || fallbackService;
}

function groupFacts(
  facts: ReportFact[],
  grouping: ReportGrouping,
  metricIds: string[],
  ctxBase: Omit<AggregateContext, 'facts' | 'appointments' | 'retentionMode'>,
  labels: { unassignedPractitioner: string; unassignedService: string; subtotalPrefix: string },
  workingHours?: PractitionerWorkingHoursContext[],
): ReportRow[] {
  const treatmentFacts = facts.filter(isAppointment);
  const by = (rows: ReportFact[], keyFn: (fact: ReportFact) => string) => {
    const map = new Map<string, ReportFact[]>();
    for (const fact of rows) {
      const key = keyFn(fact);
      const list = map.get(key);
      if (list) list.push(fact);
      else map.set(key, [fact]);
    }
    return map;
  };

  const practitionerGroups = by(facts, practitionerKey);
  const serviceGroups = by(treatmentFacts, serviceKey);

  if (grouping === 'practitioner') {
    return [...practitionerGroups.entries()].map(([id, group]) =>
      makeRow(
        {
          id: `p:${id}`,
          label: groupLabel(
            group,
            'practitioner',
            labels.unassignedPractitioner,
            labels.unassignedService,
          ),
          depth: 0,
          grouping: 'practitioner',
          practitionerId: id === UNASSIGNED_PRACTITIONER_ID ? null : id,
          serviceId: null,
          children: [],
          facts: group,
          ctxBase: {
            ...ctxBase,
            working: sumWorking(workingHours, id === UNASSIGNED_PRACTITIONER_ID ? [] : [id]),
            retentionMode: 'practitioner',
          },
        },
        metricIds,
      ),
    );
  }

  if (grouping === 'service') {
    return [...serviceGroups.entries()].map(([id, group]) =>
      makeRow(
        {
          id: `s:${id}`,
          label: groupLabel(
            group,
            'service',
            labels.unassignedPractitioner,
            labels.unassignedService,
          ),
          depth: 0,
          grouping: 'service',
          practitionerId: null,
          serviceId: id === UNASSIGNED_SERVICE_ID ? null : id,
          children: [],
          facts: group,
          ctxBase: {
            ...ctxBase,
            retentionMode: 'clinic',
          },
        },
        metricIds,
      ),
    );
  }

  if (grouping === 'practitioner_service') {
    return [...practitionerGroups.entries()].map(([pid, group]) => {
      const serviceMap = new Map<string, ReportFact[]>();
      for (const fact of group) {
        if (!isAppointment(fact)) continue;
        const key = serviceKey(fact);
        const list = serviceMap.get(key);
        if (list) list.push(fact);
        else serviceMap.set(key, [fact]);
      }
      const children = [...serviceMap.entries()].map(([sid, childFacts]) =>
        makeRow(
          {
            id: `p:${pid}:s:${sid}`,
            label: groupLabel(
              childFacts,
              'service',
              labels.unassignedPractitioner,
              labels.unassignedService,
            ),
            depth: 1,
            grouping: 'service',
            practitionerId: pid === UNASSIGNED_PRACTITIONER_ID ? null : pid,
            serviceId: sid === UNASSIGNED_SERVICE_ID ? null : sid,
            children: [],
            facts: childFacts,
            ctxBase: {
              ...ctxBase,
              working: sumWorking(workingHours, pid === UNASSIGNED_PRACTITIONER_ID ? [] : [pid]),
              retentionMode: 'practitioner',
            },
          },
          metricIds,
        ),
      );
      const parent = makeRow(
        {
          id: `p:${pid}`,
          label: groupLabel(
            group,
            'practitioner',
            labels.unassignedPractitioner,
            labels.unassignedService,
          ),
          depth: 0,
          grouping: 'practitioner',
          practitionerId: pid === UNASSIGNED_PRACTITIONER_ID ? null : pid,
          serviceId: null,
          children,
          facts: group,
          ctxBase: {
            ...ctxBase,
            working: sumWorking(workingHours, pid === UNASSIGNED_PRACTITIONER_ID ? [] : [pid]),
            retentionMode: 'practitioner',
          },
        },
        metricIds,
      );
      parent.children = [
        ...children,
        makeRow(
          {
            id: `p:${pid}:total`,
            label: `${labels.subtotalPrefix} ${parent.label}`,
            depth: 1,
            grouping: 'subtotal',
            practitionerId: parent.practitionerId,
            serviceId: null,
            children: [],
            facts: group,
            ctxBase: {
              ...ctxBase,
              working: sumWorking(workingHours, pid === UNASSIGNED_PRACTITIONER_ID ? [] : [pid]),
              retentionMode: 'practitioner',
            },
          },
          metricIds,
        ),
      ];
      return parent;
    });
  }

  // service_practitioner
  return [...serviceGroups.entries()].map(([sid, group]) => {
    const practitionerMap = new Map<string, ReportFact[]>();
    for (const fact of group) {
      const key = practitionerKey(fact);
      const list = practitionerMap.get(key);
      if (list) list.push(fact);
      else practitionerMap.set(key, [fact]);
    }
    const children = [...practitionerMap.entries()].map(([pid, childFacts]) =>
      makeRow(
        {
          id: `s:${sid}:p:${pid}`,
          label: groupLabel(
            childFacts,
            'practitioner',
            labels.unassignedPractitioner,
            labels.unassignedService,
          ),
          depth: 1,
          grouping: 'practitioner',
          practitionerId: pid === UNASSIGNED_PRACTITIONER_ID ? null : pid,
          serviceId: sid === UNASSIGNED_SERVICE_ID ? null : sid,
          children: [],
          facts: childFacts,
          ctxBase: {
            ...ctxBase,
            working: sumWorking(workingHours, pid === UNASSIGNED_PRACTITIONER_ID ? [] : [pid]),
            retentionMode: 'practitioner',
          },
        },
        metricIds,
      ),
    );
    const parent = makeRow(
      {
        id: `s:${sid}`,
        label: groupLabel(
          group,
          'service',
          labels.unassignedPractitioner,
          labels.unassignedService,
        ),
        depth: 0,
        grouping: 'service',
        practitionerId: null,
        serviceId: sid === UNASSIGNED_SERVICE_ID ? null : sid,
        children,
        facts: group,
        ctxBase: {
          ...ctxBase,
          retentionMode: 'clinic',
        },
      },
      metricIds,
    );
    parent.children = [
      ...children,
      makeRow(
        {
          id: `s:${sid}:total`,
          label: `${labels.subtotalPrefix} ${parent.label}`,
          depth: 1,
          grouping: 'subtotal',
          practitionerId: null,
          serviceId: parent.serviceId,
          children: [],
          facts: group,
          ctxBase: {
            ...ctxBase,
            retentionMode: 'clinic',
          },
        },
        metricIds,
      ),
    ];
    return parent;
  });
}

function flattenSortableValue(value: ReportValue): string | number {
  if (value == null) return '';
  return value;
}

export function compareReportValues(
  a: ReportValue,
  b: ReportValue,
  direction: 'asc' | 'desc',
): number {
  const dir = direction === 'asc' ? 1 : -1;
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
  return String(a).localeCompare(String(b), 'da') * dir;
}

export function sortReportRows(
  rows: ReportRow[],
  metricId: string | 'label',
  direction: 'asc' | 'desc',
): ReportRow[] {
  const sorted = [...rows].sort((a, b) => {
    if (a.grouping === 'total') return 1;
    if (b.grouping === 'total') return -1;
    if (metricId === 'label') return compareReportValues(a.label, b.label, direction);
    return compareReportValues(a.values[metricId], b.values[metricId], direction);
  });
  return sorted.map((row) => ({
    ...row,
    children: sortReportRows(
      row.children.filter((child) => child.grouping !== 'subtotal'),
      metricId,
      direction,
    ).concat(row.children.filter((child) => child.grouping === 'subtotal')),
  }));
}

export function flattenReportRows(rows: ReportRow[], expanded: Set<string>): ReportRow[] {
  const out: ReportRow[] = [];
  for (const row of rows) {
    out.push(row);
    if (row.children.length > 0 && expanded.has(row.id)) {
      out.push(...flattenReportRows(row.children, expanded));
    }
  }
  return out;
}

function identityColumns(grouping: ReportGrouping, metricIds: string[]): ReportColumn[] {
  const columns: ReportColumn[] = [];
  if (
    grouping === 'practitioner_service' ||
    grouping === 'practitioner' ||
    grouping === 'service_practitioner'
  ) {
    columns.push({
      id: 'practitioner',
      metricId: 'practitioner',
      label: 'Practitioner',
      format: 'text',
      sortable: true,
    });
  }
  if (
    grouping === 'practitioner_service' ||
    grouping === 'service' ||
    grouping === 'service_practitioner'
  ) {
    columns.push({
      id: 'service',
      metricId: 'service',
      label: 'Service',
      format: 'text',
      sortable: true,
    });
  }
  for (const id of metricIds) {
    const metric = METRIC_BY_ID[id];
    if (!metric?.column) continue;
    columns.push({
      id,
      metricId: id,
      label: metric.labelKey,
      format: metric.format,
      sortable: true,
    });
  }
  return columns;
}

export function generatePractitionerReport(input: GenerateReportInput): ReportResult {
  const metricIds = input.metricIds.filter((id) => METRIC_BY_ID[id]?.available);
  const filters = input.filters;
  const facts = applyClientFilters(input.facts, filters, input.period);
  const labels = {
    unassignedPractitioner: input.unassignedPractitionerLabel ?? 'Unassigned',
    unassignedService: input.unassignedServiceLabel ?? 'Unspecified service',
    subtotalPrefix: input.subtotalPrefix ?? 'Total —',
  };

  const clinicWorking = sumWorking(
    input.workingHours,
    facts
      .filter(isAppointment)
      .map((fact) => fact.practitioner_id)
      .filter((id): id is string => Boolean(id)),
  );

  const ctxBase = {
    allFacts: facts,
    basis: filters.revenueBasis,
    clinicWorking,
  };

  const rows = groupFacts(facts, filters.grouping, metricIds, ctxBase, labels, input.workingHours);

  const totals = makeRow(
    {
      id: 'total',
      label: input.totalLabel ?? 'Total',
      depth: 0,
      grouping: 'total',
      practitionerId: null,
      serviceId: null,
      children: [],
      facts,
      ctxBase: {
        ...ctxBase,
        working: clinicWorking,
        retentionMode: 'clinic',
      },
    },
    metricIds,
  );

  const summary: ReportSummaryCard[] = metricIds
    .filter((id) => METRIC_BY_ID[id]?.summary)
    .map((metricId) => ({ metricId, value: totals.values[metricId] ?? null }));

  const currency = facts.find((fact) => fact.currency)?.currency ?? 'DKK';

  return {
    titleKey: 'reports.types.practitioner.label',
    period: input.period,
    grouping: filters.grouping,
    revenueBasis: filters.revenueBasis,
    metricIds,
    columns: identityColumns(filters.grouping, metricIds),
    rows,
    totals,
    summary,
    facts,
    currency,
    retentionFollowUpDays: RETENTION_FOLLOW_UP_DAYS,
    empty: facts.length === 0,
  };
}

export function rowIdentityLabel(
  row: ReportRow,
  grouping: ReportGrouping,
): { practitioner: string; service: string } {
  if (grouping === 'practitioner') {
    return { practitioner: row.label, service: '' };
  }
  if (grouping === 'service') {
    return { practitioner: '', service: row.label };
  }
  if (row.grouping === 'practitioner') {
    return { practitioner: row.label, service: '' };
  }
  if (row.grouping === 'service') {
    return { practitioner: '', service: row.label };
  }
  return { practitioner: row.label, service: '' };
}

export function flattenSortableValueForTest(value: ReportValue): string | number {
  return flattenSortableValue(value);
}
